// libs
import { useEffect, memo, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

// analytics
import { onPageLoad, deliveryData, setTrackingToStorage, stringifyData } from './helpers';
import { getPincodeInfo } from 'shared/layouts/DetailsPage/components/ProductInfo/storage';
import { AnalyticsProps } from './types';
import { useSelector } from 'react-redux';
import { getProductOffers } from 'shared/store/details/selectors';

function Analytics({
  title,
  subTitle,
  sizeOptions,
  parentSKU,
  productID,
  isOneSize,
  price,
  discountedPrice,
  tags,
  review,
  isSizeGuideAvailable,
  colorOptions,
  defaultColor,
  isVideoAvailable,
  disclaimerText = '',
  areResponsibleTagsAvailable,
  deliveryDetails,
  colorConfig = '',
  type = '',
  isOutOfStock,
  imageUrl = '',
  isReturnable = false,
  categoryName = '',
  isPriceReveal,
  isMobile,
  currency,
  discountPercentage,
  categoryLevels,
  selectedSizeName,
  selectedSKUID,
  productBadge,
  urgencyNudgeEnabled,
  urgencyNudgeData,
  isFomoTimerVisible,
  isSalePriceLive,
  isViewSimilarAvailable,
  pdpSections,
  isAplusContentAvailable,
  isModelDetailsAvailable,
  isNewSizeChartEnabled,
  isSizeChartDataAvailable,
  isBrandBookData,
  isGamificationEnabled,
  isGamificationAggresive,
  inventoryNudge = '',
  isCategoryLaunchingSoon,
  specialTag,
  isSaleParticipating,
  returnNudgeApplicable,
  returnNudgeVisible,
  catalog_tag,
}: AnalyticsProps) {
  const { id: unSafeId } = useParams<{ id: string; }>();
  const routeID = unSafeId && escape(unSafeId);
  const { pincode: pincodeOnPdp } = getPincodeInfo();

  const pdpOffer = useSelector(getProductOffers);
  const { onSale, specialTag: pdpSpecialTags } = pdpOffer;

  let isSpecialTagsPresent: boolean = false;
  let specialTagsName: string = '';
  let specialTagType: string = '';
  let priceParticipatingSkuType: string = '';
  let isPriceParticipatingSku: boolean = false;

  if (isSaleParticipating) {
    priceParticipatingSkuType = 'MRP/DP';
    isPriceParticipatingSku = true;
  } else if (onSale) {
    priceParticipatingSkuType = 'CART Rule';
    isPriceParticipatingSku = true;
  }

  if (!isEmpty(specialTag)) {
    specialTagsName = specialTag?.type || "";
    specialTagType = 'MRP/DP';
  } else if (pdpSpecialTags?.type) {
    specialTagsName = pdpSpecialTags?.type || "";
    specialTagType = 'CART Rule';
  }

  // stringifying all pdp object to stop useEffect from multiple re-renders
  const { tagsStr, availableSizes, colorVariants, outOfStockSize, categoryLevelString } =
    useMemo(() => {
      return stringifyData({
        tags,
        options: get(sizeOptions, 'options', []),
        colorOptions,
        defaultColor,
        categoryLevels,
      });
    }, [categoryLevels, colorOptions, defaultColor, sizeOptions, tags]);
  /**
   * NOTE: Please pass only strings / numbers to useEffect onPageload
   * to avoid multiple page view calls
   */

  useEffect(() => {
    if (!deliveryDetails.deliveryDataForSKUs) return;
    const deliveryDataObject = deliveryData({
      deliveryDataForSKUs: deliveryDetails?.deliveryDataForSKUs?.details,
    });
    setTrackingToStorage(deliveryDataObject);
  }, [deliveryDetails, routeID]);

  const firePageLoad = useCallback(() => {
    if (isEmpty(productID) || productID !== routeID) return;

    const {
      star_rating: starRating,
      star_rating_count: starRatingCount,
      review_count: reviewCount,
    } = review;

    const data = {
      title,
      subTitle,
      parentSKU,
      productID,
      isOneSize,
      price,
      isSizeGuideAvailable,
      isVideoAvailable,
      availableSizes,
      colorVariants,
      tags: tagsStr,
      reviewStarRating: starRating,
      reviewStarRatingCount: starRatingCount,
      reviewCount: reviewCount,
      salePrice: discountedPrice,
      disclaimerTextShown: !isEmpty(disclaimerText),
      areResponsibleTagsAvailable,
      outOfStockSize,
      colorConfig,
      type,
      isOutOfStock,
      imageUrl,
      isReturnable,
      categoryName,
      isPriceReveal,
      isMobile,
      currency,
      discountPercentage,
      categoryLevelString,
      selectedSizeName,
      selectedSKUID,
      productBadge,
      pincodeOnPdp,
      urgencyNudgeData: {
        text: urgencyNudgeData?.tag,
        identifier: urgencyNudgeData?.tagIndentifier,
        enabled: urgencyNudgeEnabled && isMobile,
      },
      isFomoTimerVisible,
      isSalePriceLive,
      isViewSimilarAvailable,
      sizeSelectorVisible: sizeOptions?.options.length,
      isDetailsSectionAvailable: !isEmpty(pdpSections),
      isAplusContentAvailable,
      isModelDetailsAvailable,
      isNewSizeChartEnabled,
      isSizeChartDataAvailable,
      isBrandBookData,
      isGamificationEnabled,
      isGamificationAggresive,
      inventoryNudge,
      specialTagEnabled: isSpecialTagsPresent,
      ...(isSpecialTagsPresent ? { specialTagName: specialTagsName } : {}),
      isCategoryLaunchingSoon: isCategoryLaunchingSoon || false,
      isPriceParticipatingSku: isPriceParticipatingSku,
      ...(isSpecialTagsPresent ? { specialTagType } : {}),
      ...(isPriceParticipatingSku && { priceParticipatingSkuType }),
      returnNudgeApplicable,
      returnNudgeVisible,
      catalog_tag,
    };

    onPageLoad(data);
  }, [
    productID,
    routeID,
    review,
    title,
    subTitle,
    parentSKU,
    isOneSize,
    price,
    isSizeGuideAvailable,
    isVideoAvailable,
    availableSizes,
    colorVariants,
    tagsStr,
    discountedPrice,
    disclaimerText,
    areResponsibleTagsAvailable,
    outOfStockSize,
    colorConfig,
    type,
    isOutOfStock,
    imageUrl,
    isReturnable,
    categoryName,
    isPriceReveal,
    isMobile,
    currency,
    discountPercentage,
    categoryLevelString,
    selectedSizeName,
    selectedSKUID,
    productBadge,
    pincodeOnPdp,
    urgencyNudgeData.tag,
    urgencyNudgeData.tagIndentifier,
    urgencyNudgeEnabled,
    isFomoTimerVisible,
    isSalePriceLive,
    isViewSimilarAvailable,
    sizeOptions.options.length,
    pdpSections,
    isAplusContentAvailable,
    isModelDetailsAvailable,
    isNewSizeChartEnabled,
    isSizeChartDataAvailable,
    isBrandBookData,
    isGamificationEnabled,
    isGamificationAggresive,
    inventoryNudge,
    isCategoryLaunchingSoon,
    isPriceParticipatingSku,
    isSpecialTagsPresent,
    priceParticipatingSkuType,
    returnNudgeApplicable,
    returnNudgeVisible,
    catalog_tag,
    specialTagType,
    specialTagsName,
  ]);

  useEffect(() => {
    firePageLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

export default memo(Analytics);
